import { useState } from 'react';
import {
  Title,
  Text,
  Stack,
  Paper,
  Table,
  Button,
  Group,
  ActionIcon,
  Modal,
  TextInput,
  Textarea,
  Badge,
  Skeleton,
  Alert,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconBuilding,
  IconAlertTriangle,
} from '@tabler/icons-react';

import { useSites, useCreateSite, useUpdateSite, useDeleteSite, type Site } from '@/hooks';

export function SitesPage() {
  const { data: sites, isLoading, isError } = useSites();
  const createSite = useCreateSite();
  const updateSite = useUpdateSite();
  const deleteSite = useDeleteSite();

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure();
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure();
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [deletingSite, setDeletingSite] = useState<Site | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    buildingCode: '',
    subnets: '',
    timezone: 'Europe/Ljubljana',
  });

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      buildingCode: '',
      subnets: '',
      timezone: 'Europe/Ljubljana',
    });
    setEditingSite(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    openModal();
  };

  const handleOpenEdit = (site: Site) => {
    setEditingSite(site);
    setFormData({
      code: site.code,
      name: site.name,
      buildingCode: site.buildingCode || '',
      subnets: site.subnets.join('\n'),
      timezone: site.timezone || 'Europe/Ljubljana',
    });
    openModal();
  };

  const handleOpenDelete = (site: Site) => {
    setDeletingSite(site);
    openDeleteModal();
  };

  const handleSubmit = async () => {
    const subnetsArray = formData.subnets
      .split('\n')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    const data = {
      code: formData.code,
      name: formData.name,
      buildingCode: formData.buildingCode || null,
      subnets: subnetsArray,
      timezone: formData.timezone,
    };

    try {
      if (editingSite) {
        await updateSite.mutateAsync({ id: editingSite.id, data });
        notifications.show({
          title: 'Site updated',
          message: `${formData.name} has been updated`,
          color: 'green',
        });
      } else {
        await createSite.mutateAsync(data);
        notifications.show({
          title: 'Site created',
          message: `${formData.name} has been created`,
          color: 'green',
        });
      }
      closeModal();
      resetForm();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save site',
        color: 'red',
      });
    }
  };

  const handleDelete = async () => {
    if (!deletingSite) return;

    try {
      await deleteSite.mutateAsync(deletingSite.id);
      notifications.show({
        title: 'Site deleted',
        message: `${deletingSite.name} has been deleted`,
        color: 'green',
      });
      closeDeleteModal();
      setDeletingSite(null);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete site',
        color: 'red',
      });
    }
  };

  return (
    <>
      <Stack gap="lg">
        {/* Page header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Sites</Title>
            <Text c="dimmed" size="sm">
              Manage locations and buildings
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
            Add Site
          </Button>
        </Group>

        {/* Sites table */}
        <Paper withBorder>
          {isLoading ? (
            <Stack p="md" gap="sm">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} height={50} />
              ))}
            </Stack>
          ) : isError ? (
            <Alert color="red" m="md">
              Failed to load sites
            </Alert>
          ) : sites?.length === 0 ? (
            <Stack align="center" p="xl" gap="md">
              <IconBuilding size={48} color="gray" />
              <Text c="dimmed">No sites configured</Text>
              <Button variant="light" onClick={handleOpenCreate}>
                Create your first site
              </Button>
            </Stack>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Code</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Building</Table.Th>
                  <Table.Th>Subnets</Table.Th>
                  <Table.Th>Timezone</Table.Th>
                  <Table.Th w={100}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sites?.map((site) => (
                  <Table.Tr key={site.id}>
                    <Table.Td>
                      <Text fw={500} ff="monospace" size="sm">
                        {site.code}
                      </Text>
                    </Table.Td>
                    <Table.Td>{site.name}</Table.Td>
                    <Table.Td>
                      {site.buildingCode ? (
                        <Badge variant="light" size="sm">
                          {site.buildingCode}
                        </Badge>
                      ) : (
                        <Text c="dimmed" size="sm">-</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {site.subnets.length > 0 ? (
                        <Group gap="xs">
                          {site.subnets.slice(0, 2).map((subnet, i) => (
                            <Badge key={i} variant="outline" size="xs">
                              {subnet}
                            </Badge>
                          ))}
                          {site.subnets.length > 2 && (
                            <Badge variant="outline" size="xs" color="gray">
                              +{site.subnets.length - 2}
                            </Badge>
                          )}
                        </Group>
                      ) : (
                        <Text c="dimmed" size="sm">-</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{site.timezone || '-'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => handleOpenEdit(site)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="red"
                          onClick={() => handleOpenDelete(site)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </Stack>

      {/* Create/Edit Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => {
          closeModal();
          resetForm();
        }}
        title={editingSite ? 'Edit Site' : 'Create Site'}
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Code"
            placeholder="BLDG-A-F1"
            description="Short unique identifier"
            required
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
          />
          <TextInput
            label="Name"
            placeholder="Building A - Floor 1"
            description="Display name for the site"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextInput
            label="Building Code"
            placeholder="BLDG-A"
            description="Group sites by building (optional)"
            value={formData.buildingCode}
            onChange={(e) => setFormData({ ...formData, buildingCode: e.target.value })}
          />
          <Textarea
            label="Subnets"
            placeholder="10.10.1.0/24&#10;10.10.2.0/24"
            description="One subnet per line (for auto-assignment)"
            minRows={3}
            value={formData.subnets}
            onChange={(e) => setFormData({ ...formData, subnets: e.target.value })}
          />
          <TextInput
            label="Timezone"
            placeholder="Europe/Ljubljana"
            value={formData.timezone}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => { closeModal(); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={createSite.isPending || updateSite.isPending}
            >
              {editingSite ? 'Save Changes' : 'Create Site'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => {
          closeDeleteModal();
          setDeletingSite(null);
        }}
        title="Delete Site"
        size="sm"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
            Are you sure you want to delete <strong>{deletingSite?.name}</strong>?
            This action cannot be undone.
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { closeDeleteModal(); setDeletingSite(null); }}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteSite.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
